import { useState } from "react";
import { Siren, Phone, ShieldCheck } from "lucide-react";
import { PublicHeader, PublicFooter } from "../components/PublicShell";
import { useEmergencies } from "../lib/emergencies";
import { ProtocolList } from "../components/emergency/ProtocolList";
import { useSeo } from "../lib/seo";

const KIT = ["Oxygen cylinder + masks", "AED (defibrillator)", "Adrenaline 1 mg/mL", "Glucagon 1 mg", "Oral glucose", "GTN spray", "Aspirin 300 mg", "Salbutamol inhaler + spacer", "Buccal midazolam", "Bag-valve-mask & suction"];

function Content() {
  const data = useEmergencies();
  const [open, setOpen] = useState("");
  return (
    <div data-testid="emergency-page">
      <span className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full bg-red-100 text-red-700 text-xs font-bold"><Siren className="w-3.5 h-3.5" /> Medical emergencies in the dental surgery</span>
      <h1 className="font-heading text-4xl sm:text-5xl font-bold tracking-tight text-[#0B2545] mt-4">Emergency protocols</h1>
      <p className="text-slate-600 mt-3 max-w-2xl">Step-by-step actions, drug doses and what not to do — for collapse, anaphylaxis, seizures, hypoglycaemia, chest pain, stroke and more. Based on Resuscitation Council UK, ERC 2025, AHA 2025 and BNF.</p>
      <div className="mt-6 flex items-center gap-2 rounded-xl bg-red-600 text-white px-4 py-3 font-semibold" data-testid="emergency-call-line">
        <Phone className="w-5 h-5 shrink-0" /> Call for help first — 997 / 911 (Saudi Arabia) · 999 (UK) · 112 (EU) · 911 (USA)
      </div>
      <div className="grid lg:grid-cols-[1fr_260px] gap-6 mt-6">
        <div>{data ? <ProtocolList protocols={data.protocols} open={open} onOpen={setOpen} /> : <p className="text-sm text-slate-500">Loading…</p>}</div>
        <aside className="rounded-xl border border-slate-200 bg-white p-4 h-fit" data-testid="emergency-kit">
          <p className="font-heading font-semibold text-[#0B2545] flex items-center gap-2"><ShieldCheck className="w-4 h-4 text-[#16B5A6]" /> Minimum emergency kit</p>
          <ul className="mt-2 space-y-1.5 text-sm text-slate-700">{KIT.map((k) => <li key={k}>• {k}</li>)}</ul>
          <p className="text-xs text-slate-500 mt-3">Check drugs and equipment weekly; train the whole team every year (Resuscitation Council UK — Primary dental care).</p>
        </aside>
      </div>
      <p className="text-xs text-slate-500 mt-6">These protocols support — and do not replace — formal emergency training and local protocols. Doses must be confirmed against the current BNF / national formulary.</p>
    </div>
  );
}

export function AppEmergency() {
  return <Content />;
}

export default function Emergency() {
  useSeo({
    title: "Medical Emergencies in Dental Practice — Protocols & Drug Doses | DentiCision",
    description: "Step-by-step dental-surgery protocols for cardiac arrest, anaphylaxis, fainting, hypoglycaemia, seizures, asthma, chest pain, stroke, adrenal crisis and choking — with doses from Resuscitation Council UK, ERC 2025, AHA 2025 and BNF.",
    path: "/emergency",
  });
  return (
    <div className="min-h-screen bg-white">
      <PublicHeader />
      <div className="max-w-6xl mx-auto px-4 sm:px-6 py-12"><Content /></div>
      <PublicFooter />
    </div>
  );
}
